/**
 * Every version this workflow has been. An approval keeps its own copy of the
 * chain, so a change here never reaches one in flight; this is where "what did
 * it look like in March, and who changed it" is answered.
 */

import { useState } from 'react';
import { useWorkflowVersions, type WorkflowVersion } from './approvalsData';
import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

interface Named { id: string; name: string }

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'date not recorded');
export const whoChanged = (v: WorkflowVersion) => v.by.email ?? v.by.name ?? 'nobody: it was already like this';

function forText(v: WorkflowVersion, types: readonly Named[], departments: readonly Named[]): string {
  if (v.isDefault) return 'everything no other workflow covered';
  const names = (ids: string[], all: readonly Named[]) => ids.map((id) => all.find((x) => x.id === id)?.name ?? 'a removed item').join(', ');
  const t = names(v.covers.documentTypeIds, types), d = names(v.covers.departmentIds, departments);
  if (t && d) return `${t} from ${d}`;
  return t || (d ? `anything from ${d}` : 'nothing');
}

export function WorkflowHistory({ workflowId, versionCount = 0, types, departments }: { workflowId: string; versionCount?: number; types: readonly Named[]; departments: readonly Named[] }) {
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const versions = useWorkflowVersions(workflowId, shown);
  const list = versions.data?.versions ?? [];

  return (
    <section className={styles.section} aria-labelledby="history-title">
      {/* The count is already on the workflow record the list callable
          returns, so naming it here costs no extra read. */}
      <h2 className={styles.sectionTitle} id="history-title">
        History{versionCount > 0 ? ` · ${versionCount} ${versionCount === 1 ? 'version' : 'versions'}` : ''}
      </h2>
      <p className={`${own.itemMeta} ${own.explain}`}>
        Changes apply only to future submissions. Approvals already under way keep the workflow steps and settings
        they started with, and record which version that was. Every change made here is kept below, and a version is
        never edited.
      </p>
      <button type="button" className={styles.btn} aria-expanded={shown} aria-controls="workflow-history" onClick={() => setShown((v) => !v)}>
        {shown ? 'Hide the history' : 'Show the history'}
      </button>

      {shown && (
        <div id="workflow-history">
          {versions.isLoading ? <Spinner label="Loading the history" />
            : versions.error ? (
              <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
                The history could not be loaded. {isCallFailure(versions.error) || versions.error instanceof Error ? versions.error.message : ''}
              </div>
            ) : list.length === 0 ? (
              <p className={own.itemMeta}>No changes have been recorded yet. History is kept from the first change made after 20 September 2026.</p>
            ) : (
              <ol className={own.versions} aria-label="Versions, newest first">
                {list.map((v) => (
                  <li key={v.version} className={own.version}>
                    <div className={own.versionHead}>
                      <span className={own.versionNo}>Version {v.version}</span>
                      <span className={own.versionWhat}>{v.change}</span>
                      <button type="button" className={own.linkBtn} aria-expanded={open === v.version}
                        aria-label={`${open === v.version ? 'Hide' : 'Show'} version ${v.version} as it stood`}
                        onClick={() => setOpen(open === v.version ? null : v.version)}>
                        {open === v.version ? 'Hide' : 'Show it as it stood'}
                      </button>
                    </div>
                    <div className={own.itemMeta}>{when(v.at)} · {whoChanged(v)}</div>
                    {open === v.version && (
                      <div className={own.versionBody}>
                        <p className={own.itemMeta}>
                          Named &ldquo;{v.name}&rdquo;. For {forText(v, types, departments)}.
                          {v.isActive ? '' : ' Retired.'} {v.authorsMayChoose ? '' : 'Closed to being chosen by authors.'}
                        </p>
                        {v.steps.length === 0 ? <p className={own.itemMeta}>It had no steps.</p> : (
                          <ol className={own.exampleSteps} aria-label={`The steps of version ${v.version}`}>
                            {v.steps.map((s) => (
                              <li key={s.stepId}>
                                <span className={own.exampleOrder} aria-hidden="true">{s.order}</span>
                                <span><span className="vh">Step {s.order}: </span>{s.name}
                                  <span className={own.exampleRole}> · {s.roleId && isRole(s.roleId) ? ROLE_DISPLAY_NAMES[s.roleId] : 'No role assigned'}</span>
                                </span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
        </div>
      )}
    </section>
  );
}
